import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

/** One entry in the agent's model picker, as served by the backend. */
export interface AgentModelOption {
  /** Azure AI Foundry deployment name — sent to the model endpoint verbatim. */
  id: string
  /** Tier name shown in the picker ("Best", "Good"); never a model name. */
  label: string
  /** One line saying what the tier means. */
  description?: string
  /**
   * Cost relative to the default model: 1 = what you normally pay, 2 = twice
   * that, 0.4 = 40% of it. Shown beside the label so the cost of a choice is
   * visible at the moment of choosing.
   */
  costMultiplier: number
}

/**
 * Fetch the agent's model offering.
 *
 * The list lives on the server (goapi/pkg/llm/agentmodels.go) and the same list
 * gates every incoming message, so the picker cannot offer something the server
 * would refuse — nor can the server quietly accept something the picker never
 * showed. There is deliberately no hardcoded copy on this side to fall back to:
 * a stale local list is the exact drift this endpoint exists to prevent.
 */
export async function fetchAgentModels(): Promise<{ models: AgentModelOption[]; defaultModel: string }> {
  const token = (axios.defaults.headers.common['Authorization'] as string | undefined)?.replace(/^Bearer\s+/, '') ?? ''
  const res = await fetch(`${API_BASE_URL}/agent/models`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
  if (!res.ok) throw new Error(`agent models ${res.status}`)
  const data = await res.json()
  return { models: data.models ?? [], defaultModel: data.default ?? '' }
}
